import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Define types
export interface Question {
  id: string;
  text: string;
  options: { id: string; text: string }[];
  correctOptionId: string;
}

export interface Assessment {
  id: string;
  title: string;
  description: string;
  category: string;
  duration: number; // in minutes
  questions: Question[];
  totalScore: number;
}

export interface UserAssessment {
  id: string;
  assessmentId: string;
  userId: string;
  score: number;
  completed: boolean;
  startedAt: string;
  completedAt: string | null;
  userAnswers: Record<string, string>; // questionId: selectedOptionId
}

interface AssessmentState {
  assessments: Assessment[];
  currentAssessment: Assessment | null;
  userAssessments: UserAssessment[];
  currentUserAssessment: UserAssessment | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: AssessmentState = {
  assessments: [],
  currentAssessment: null,
  userAssessments: [],
  currentUserAssessment: null,
  isLoading: false,
  error: null,
};

// Mock data - replace with actual API calls later
const mockAssessments: Assessment[] = [
  {
    id: '1',
    title: 'JavaScript Fundamentals',
    description: 'Test your knowledge of JavaScript basics',
    category: 'Web Development',
    duration: 30,
    totalScore: 100,
    questions: [
      {
        id: 'q1',
        text: 'What is JavaScript?',
        options: [
          { id: 'o1', text: 'A programming language' },
          { id: 'o2', text: 'A markup language' },
          { id: 'o3', text: 'A database' },
          { id: 'o4', text: 'A browser' }
        ],
        correctOptionId: 'o1'
      },
      {
        id: 'q2',
        text: 'Which keyword is used to declare variables in JavaScript?',
        options: [
          { id: 'o1', text: 'var' },
          { id: 'o2', text: 'int' },
          { id: 'o3', text: 'string' },
          { id: 'o4', text: 'declare' }
        ],
        correctOptionId: 'o1'
      }
    ]
  },
  {
    id: '2',
    title: 'React Components',
    description: 'Test your knowledge of React components',
    category: 'Web Development',
    duration: 45,
    totalScore: 100,
    questions: [
      {
        id: 'q1',
        text: 'What is a React component?',
        options: [
          { id: 'o1', text: 'A reusable piece of UI' },
          { id: 'o2', text: 'A JavaScript file' },
          { id: 'o3', text: 'A CSS file' },
          { id: 'o4', text: 'A database query' }
        ],
        correctOptionId: 'o1'
      }
    ]
  }
];

// Mock API calls
const mockFetchAssessmentsApi = async (): Promise<Assessment[]> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Return mock data
  return mockAssessments;
};

const mockFetchAssessmentApi = async (id: string): Promise<Assessment> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Find assessment by ID
  const assessment = mockAssessments.find(assessment => assessment.id === id);
  
  if (!assessment) {
    throw new Error('Assessment not found');
  }
  
  return assessment;
};

const mockStartAssessmentApi = async (assessmentId: string): Promise<UserAssessment> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Return started assessment
  return {
    id: `user-assessment-${Date.now()}`,
    assessmentId,
    userId: '1', // Mock user ID
    score: 0,
    completed: false,
    startedAt: new Date().toISOString(),
    completedAt: null,
    userAnswers: {}
  };
};

const mockCompleteAssessmentApi = async (
  assessmentId: string, 
  userAnswers: Record<string, string>
): Promise<UserAssessment> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Find assessment
  const assessment = mockAssessments.find(a => a.id === assessmentId);
  
  if (!assessment) {
    throw new Error('Assessment not found');
  }
  
  // Calculate score
  let score = 0;
  assessment.questions.forEach(question => {
    if (userAnswers[question.id] === question.correctOptionId) {
      score += assessment.totalScore / assessment.questions.length;
    }
  });
  
  // Return completed assessment
  return {
    id: `user-assessment-${Date.now()}`,
    assessmentId,
    userId: '1', // Mock user ID
    score,
    completed: true,
    startedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(), // 10 minutes ago
    completedAt: new Date().toISOString(),
    userAnswers
  };
};

// Create async thunks
export const fetchAssessments = createAsyncThunk(
  'assessment/fetchAssessments',
  async (_, { rejectWithValue }) => {
    try {
      return await mockFetchAssessmentsApi();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch assessments');
    }
  }
);

export const fetchAssessment = createAsyncThunk(
  'assessment/fetchAssessment',
  async (id: string, { rejectWithValue }) => {
    try {
      return await mockFetchAssessmentApi(id);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch assessment');
    }
  }
);

export const startAssessment = createAsyncThunk(
  'assessment/startAssessment',
  async (assessmentId: string, { rejectWithValue }) => {
    try {
      return await mockStartAssessmentApi(assessmentId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to start assessment');
    }
  }
);

export const completeAssessment = createAsyncThunk(
  'assessment/completeAssessment',
  async ({ assessmentId, userAnswers }: { assessmentId: string; userAnswers: Record<string, string> }, { rejectWithValue }) => {
    try {
      return await mockCompleteAssessmentApi(assessmentId, userAnswers);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to complete assessment');
    }
  }
);

// Create slice
const assessmentSlice = createSlice({
  name: 'assessment',
  initialState,
  reducers: {
    submitAnswer: (state, action: PayloadAction<{ questionId: string; optionId: string }>) => {
      if (state.currentUserAssessment) {
        state.currentUserAssessment = {
          ...state.currentUserAssessment,
          userAnswers: {
            ...state.currentUserAssessment.userAnswers,
            [action.payload.questionId]: action.payload.optionId,
          },
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch assessments
      .addCase(fetchAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assessments = action.payload;
        state.error = null;
      })
      .addCase(fetchAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch assessment
      .addCase(fetchAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAssessment = action.payload;
        state.error = null;
      })
      .addCase(fetchAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Start assessment
      .addCase(startAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUserAssessment = action.payload;
        state.userAssessments = [...state.userAssessments, action.payload];
        state.error = null;
      })
      .addCase(startAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Complete assessment
      .addCase(completeAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUserAssessment = action.payload;
        
        // Update in userAssessments array
        state.userAssessments = state.userAssessments.map(assessment => 
          assessment.id === action.payload.id ? action.payload : assessment
        );
        
        state.error = null;
      })
      .addCase(completeAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { submitAnswer } = assessmentSlice.actions;

// Export reducer
export default assessmentSlice.reducer;